import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Stack } from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../../app/api/api';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import TimeRangeSelector from '../../../app/components/TimeRangeSelector';
import { AvailabilityScheduleDto } from '../../../app/data/models/common/availability';
import dayjs from 'dayjs';
import ListTitleBox from '../../../app/components/list/ListTitleBox';

export default function RestaurantOpeningHours() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingHours, setOpeningHours] = useState<AvailabilityScheduleDto[]>(
    []
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: number]: { start?: boolean; end?: boolean };
  }>({});
  const [touchedFields, setTouchedFields] = useState<{
    [key: number]: { start?: boolean; end?: boolean };
  }>({});

  useEffect(() => {
    loadOpeningHours();
  }, [restaurantId]);

  const loadOpeningHours = async () => {
    if (!restaurantId) return;
    try {
      const response = await api.Restaurant.getOpeningHours(restaurantId);

      // Initialize all 7 days
      const allDays: AvailabilityScheduleDto[] = Array.from(
        { length: 7 },
        (_, i) => ({
          dayOfWeek: i,
          isEnabled: false,
          isAllDay: false,
          startTime: null,
          endTime: null,
        })
      );

      // Update with server data if available
      if (response && response.length > 0) {
        response.forEach((schedule: AvailabilityScheduleDto) => {
          const formattedSchedule = {
            ...schedule,
            startTime: schedule.startTime
              ? dayjs(schedule.startTime, 'HH:mm').format('HH:mm')
              : null,
            endTime: schedule.endTime
              ? dayjs(schedule.endTime, 'HH:mm').format('HH:mm')
              : null,
          };
          allDays[schedule.dayOfWeek] = formattedSchedule;
        });
      }

      setOpeningHours(allDays);
    } catch (error) {
      console.error('Error loading opening hours:', error);
      toast.error(t('restaurant.settings.opening-hours.load-error'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (
    index: number,
    updates: Partial<AvailabilityScheduleDto>
  ) => {
    setOpeningHours((prev) =>
      prev.map((a, i) => (i === index ? { ...a, ...updates } : a))
    );
    setHasChanges(true);
  };

  const handleTimeChange = (
    index: number,
    field: 'start' | 'end',
    value: string | null
  ) => {
    handleUpdate(index, { [`${field}Time`]: value });
  };

  const handleTimeBlur = (index: number, field: 'start' | 'end') => {
    setTouchedFields((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: true },
    }));
  };

  const validateSchedule = (schedule: AvailabilityScheduleDto) => {
    if (schedule.isEnabled && !schedule.isAllDay) {
      const errors: { start?: boolean; end?: boolean } = {};

      if (!schedule.startTime) {
        errors.start = true;
      }
      if (!schedule.endTime) {
        errors.end = true;
      }

      return Object.keys(errors).length > 0 ? errors : undefined;
    }
    return undefined;
  };

  const handleSave = async () => {
    const newValidationErrors: {
      [key: number]: { start?: boolean; end?: boolean };
    } = {};
    let hasErrors = false;

    openingHours.forEach((schedule, index) => {
      const scheduleErrors = validateSchedule(schedule);
      if (scheduleErrors) {
        newValidationErrors[index] = scheduleErrors;
        hasErrors = true;
      }
    });

    setValidationErrors(newValidationErrors);

    if (hasErrors) {
      toast.error(t('restaurant.settings.opening-hours.validation-error'));
      return;
    }

    setSaving(true);
    try {
      const formattedOpeningHours = openingHours.map((schedule) => ({
        ...schedule,
        startTime:
          schedule.isEnabled && !schedule.isAllDay && schedule.startTime
            ? dayjs(schedule.startTime, 'HH:mm').format('HH:mm:00')
            : null,
        endTime:
          schedule.isEnabled && !schedule.isAllDay && schedule.endTime
            ? dayjs(schedule.endTime, 'HH:mm').format('HH:mm:00')
            : null,
      }));

      await api.Restaurant.updateOpeningHours(restaurantId, {
        availabilitySchedules: formattedOpeningHours,
      });
      toast.success(t('restaurant.settings.opening-hours.save-success'));
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving opening hours:', error);
      toast.error(t('restaurant.settings.opening-hours.save-error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <LoadingComponent
        message={t('restaurant.settings.opening-hours.loading')}
      />
    );

  return (
    <Box>
      <ListTitleBox
        title={t('restaurant.settings.opening-hours.title')}
        primaryButtonDisabled={!hasChanges || saving}
        primaryButtonText={t('common.save')}
        onPrimaryClick={handleSave}
        onBackClick={() => navigate(-1)}
      />

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {openingHours.map((schedule, index) => (
            <TimeRangeSelector
              key={index}
              day={t(`common.days.${schedule.dayOfWeek}`)}
              isEnabled={schedule.isEnabled}
              isAllDay={schedule.isAllDay}
              startTime={
                schedule.startTime ? dayjs(schedule.startTime, 'HH:mm') : null
              }
              endTime={
                schedule.endTime ? dayjs(schedule.endTime, 'HH:mm') : null
              }
              error={{
                start:
                  schedule.isEnabled &&
                  !schedule.isAllDay &&
                  (touchedFields[index]?.start ||
                    validationErrors[index]?.start) &&
                  !schedule.startTime,
                end:
                  schedule.isEnabled &&
                  !schedule.isAllDay &&
                  (touchedFields[index]?.end || validationErrors[index]?.end) &&
                  !schedule.endTime,
              }}
              helperText={t('restaurant.settings.opening-hours.time-required')}
              onEnabledChange={(enabled) =>
                handleUpdate(index, { isEnabled: enabled })
              }
              onAllDayChange={(allDay) =>
                handleUpdate(index, { isAllDay: allDay })
              }
              onStartTimeChange={(time) =>
                handleTimeChange(
                  index,
                  'start',
                  time ? time.format('HH:mm:00') : null
                )
              }
              onEndTimeChange={(time) =>
                handleTimeChange(
                  index,
                  'end',
                  time ? time.format('HH:mm:00') : null
                )
              }
              onStartTimeBlur={() => handleTimeBlur(index, 'start')}
              onEndTimeBlur={() => handleTimeBlur(index, 'end')}
            />
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
